import React, { useState, useEffect, useRef } from 'react';
import { aboutService, AboutContent } from '../../services/aboutService';
import LoadingSpinner from '../ui/LoadingSpinner';
import { TrashIcon } from '@heroicons/react/24/outline';

export const AboutManager: React.FC = () => {
  const [aboutContent, setAboutContent] = useState<AboutContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadAboutContent();
  }, []);
  
  // Clear success message after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const loadAboutContent = async () => {
    try {
      setIsLoading(true);
      const data = await aboutService.getAboutContent();
      setAboutContent(data);
      setError(null);
    } catch (err) {
      setError('Failed to load about page content');
      console.error('Error loading about content:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (!aboutContent) return;
    
    setAboutContent({
      ...aboutContent,
      [name]: value
    });
  };

  const handleListItemChange = (index: number, value: string, listName: 'valuesList' | 'processList') => {
    if (!aboutContent) return;
    
    const newList = [...aboutContent[listName]];
    newList[index] = value;
    
    setAboutContent({
      ...aboutContent,
      [listName]: newList
    });
  };

  const handleAddListItem = (listName: 'valuesList' | 'processList') => {
    if (!aboutContent) return;
    
    setAboutContent({
      ...aboutContent,
      [listName]: [...aboutContent[listName], '']
    });
  };

  const handleRemoveListItem = (index: number, listName: 'valuesList' | 'processList') => {
    if (!aboutContent) return;
    
    const newList = [...aboutContent[listName]];
    newList.splice(index, 1);
    
    setAboutContent({
      ...aboutContent,
      [listName]: newList
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!aboutContent) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      await aboutService.updateAboutContent(aboutContent);
      setSaveSuccess(true);
    } catch (err) {
      setError('Failed to save about page content');
      console.error('Error saving about content:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !aboutContent) return;
    
    const file = files[0];
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }
    
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size should be less than 5MB');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadError(null);
      
      const imageUrl = await aboutService.uploadProfileImage(file);
      
      setAboutContent({
        ...aboutContent,
        profileImage: imageUrl
      });
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setUploadError('Failed to upload image');
      console.error('Error uploading image:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!aboutContent || !aboutContent.profileImage) return;
    
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadError(null);
      
      await aboutService.deleteProfileImage();
      
      setAboutContent({
        ...aboutContent,
        profileImage: null
      });
    } catch (err) {
      setUploadError('Failed to delete image');
      console.error('Error deleting image:', err);
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!aboutContent) {
    return (
      <div className="bg-red-50 p-4 rounded-md text-red-700">
        Unable to load about page content.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold mb-6">About Page Content</h2>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md">
          About page content saved successfully!
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Profile Image Section */}
        <div className="mb-8 p-6 border rounded-lg bg-gray-50">
          <h3 className="text-lg font-medium text-gray-700 mb-4">Profile Image</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team or Company Image
                </label>
                <div className="mt-1 flex items-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-medium
                      file:bg-accent-50 file:text-accent-700
                      hover:file:bg-accent-100"
                    disabled={isUploading}
                  />
                  {isUploading && <LoadingSpinner className="ml-2 w-5 h-5" />}
                </div>
                {uploadError && (
                  <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                )}
              </div>
              
              {aboutContent.profileImage && (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleDeleteImage}
                    className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    disabled={isUploading}
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Remove Image
                  </button>
                </div>
              )}
            </div>
            
            <div>
              {aboutContent.profileImage ? (
                <div className="border rounded-md overflow-hidden relative pt-[75%]">
                  <img 
                    src={aboutContent.profileImage} 
                    alt="Company profile" 
                    className="absolute top-0 left-0 w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="border rounded-md bg-gray-100 flex items-center justify-center h-48">
                  <p className="text-gray-500">No image uploaded</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Company Description */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b">Company Description</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="companyDescription">
              Main Description
            </label>
            <textarea
              id="companyDescription"
              name="companyDescription"
              value={aboutContent.companyDescription}
              onChange={handleInputChange}
              rows={4}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="teamDescription">
              Team Description
            </label>
            <textarea
              id="teamDescription"
              name="teamDescription"
              value={aboutContent.teamDescription}
              onChange={handleInputChange}
              rows={4}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
        </div>
        
        {/* Values Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b">Values Section</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="valuesTitle">
              Section Title
            </label>
            <input
              type="text"
              id="valuesTitle"
              name="valuesTitle"
              value={aboutContent.valuesTitle}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Values List
            </label>
            {aboutContent.valuesList.map((value, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleListItemChange(index, e.target.value, 'valuesList')}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => handleRemoveListItem(index, 'valuesList')}
                  className="ml-2 p-1 text-red-600 hover:text-red-800"
                  disabled={aboutContent.valuesList.length <= 1}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddListItem('valuesList')}
              className="mt-2 px-3 py-1 text-sm text-accent-600 hover:text-accent-800 border border-accent-300 rounded-md"
            >
              + Add Value
            </button>
          </div>
        </div>
        
        {/* Process Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b">Process Section</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="processTitle">
              Section Title
            </label>
            <input
              type="text"
              id="processTitle"
              name="processTitle"
              value={aboutContent.processTitle}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="processDescription">
              Process Description
            </label>
            <textarea
              id="processDescription"
              name="processDescription"
              value={aboutContent.processDescription}
              onChange={handleInputChange}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Process Steps
            </label>
            {aboutContent.processList.map((step, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  value={step}
                  onChange={(e) => handleListItemChange(index, e.target.value, 'processList')}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => handleRemoveListItem(index, 'processList')}
                  className="ml-2 p-1 text-red-600 hover:text-red-800"
                  disabled={aboutContent.processList.length <= 1}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddListItem('processList')}
              className="mt-2 px-3 py-1 text-sm text-accent-600 hover:text-accent-800 border border-accent-300 rounded-md"
            >
              + Add Step
            </button>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutManager; 